import React, { useEffect, useMemo, useState } from "react";
import { useGetAllCampaignUseCasesQuery } from "../../../redux/services/campaignApi";
import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import { UseCase } from "../../../pages/Campaigns/utils/types";
import SearchIcon from "../../../assets/svg/search-icon";
import CheckIcon from "../../../assets/svg/check-icon";
import Button from "../../Button/Button";
import {
  selectCampaignTemplatesFilters,
  selectCampaignsFilters,
  setCampaignTemplatesUseCase,
  setCampaignsBranch,
  setCampaignsUseCase,
} from "../../../redux/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setCurrentPage } from "../../../redux/slices/allCampaignSlice";

const CampaignUseCaseFilter = ({
  onClose,
  campaignTemplates = false,
}: {
  onClose: () => void;
  campaignTemplates?: boolean;
}) => {
  const campaignFilters = useSelector(
    campaignTemplates ? selectCampaignTemplatesFilters : selectCampaignsFilters
  );
  const category = useSelector(
    (state: RootState) => state.filter.campaigns.category.value
  );

  const {
    data: useCases,
    isLoading,
    isFetching,
  } = useGetAllCampaignUseCasesQuery({
    category: (category as string) || undefined,
  });
  const [selectedUseCase, setSelectedUseCase] = useState({
    label: campaignFilters.useCase.displayValue,
    value: campaignFilters.useCase.value,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUseCases, setFilteredUseCases] = useState<
    SearchableDropdownOption[]
  >([]);
  const dispatch = useDispatch();

  const transformedUseCases = useMemo(() => {
    return (useCases?.use_cases ?? [])?.map((useCase: UseCase) => ({
      label: useCase?.display_name,
      value: useCase?.key,
    }));
  }, [useCases]);

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setFilteredUseCases(transformedUseCases);
      return;
    }

    const filtered = transformedUseCases.filter(
      (user: SearchableDropdownOption) =>
        user.label?.toString().toLowerCase().includes(query.toLowerCase())
    );
    setFilteredUseCases(filtered);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  useEffect(() => {
    setFilteredUseCases(transformedUseCases);
  }, [transformedUseCases]);

  const handleUseCaseSelect = (value: string, label: string) => {
    setSelectedUseCase(
      value === selectedUseCase.value
        ? { label: "", value: "" }
        : { label, value }
    );
  };

  const handleApply = () => {
    if (selectedUseCase.value === campaignFilters.useCase.value) {
      return;
    }
    dispatch(
      campaignTemplates
        ? setCampaignTemplatesUseCase({
            value: selectedUseCase.value as string,
            displayValue: selectedUseCase.label as string,
          })
        : setCampaignsUseCase({
            value: selectedUseCase.value as string,
            displayValue: selectedUseCase.label as string,
          })
    );
    dispatch(setCampaignsBranch({ value: "", displayValue: "" }));
    dispatch(setCurrentPage(1));
    onClose();
  };

  return (
    <div className="flex flex-col">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search user"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {/* Selected User Display */}
      {selectedUseCase.value && (
        <div className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-b border-border-gray dark:border-border-gray-dk">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
              {
                transformedUseCases?.find(
                  (u: SearchableDropdownOption) =>
                    u.value === selectedUseCase.value
                )?.label
              }
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="flex flex-col max-h-[600px] ">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Users
        </div>

        <div className="overflow-y-auto">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoading ? "Loading users..." : "Fetching users..."}
              </div>
            </div>
          ) : filteredUseCases?.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim() ? "No users found" : "No users available"}
              </div>
            </div>
          ) : (
            filteredUseCases
              ?.filter(
                (useCase: SearchableDropdownOption) =>
                  useCase?.value !== selectedUseCase?.value
              )
              ?.map((useCase: SearchableDropdownOption) => (
                <div
                  key={useCase.value}
                  onClick={() =>
                    handleUseCaseSelect(useCase.value, useCase?.label || "")
                  }
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                      {useCase.label}
                    </div>
                  </div>
                  {selectedUseCase.value === useCase.value && (
                    <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ml-2" />
                  )}
                </div>
              ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={selectedUseCase.value === campaignFilters.useCase.value}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default CampaignUseCaseFilter;
